/**
 * PageLoader Component
 * Unified loading state component with multiple variants for consistent UX
 */

import { useTranslation } from 'react-i18next'

interface PageLoaderProps {
  variant?: 'fullPage' | 'section' | 'inline' | 'tab'
  text?: string // Optional loading text (uses i18n key or raw text)
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function PageLoader({
  variant = 'section',
  text,
  size = 'md',
  className = '',
}: PageLoaderProps) {
  const { t } = useTranslation()

  // Try to translate the text, fallback to raw text if not a key
  const displayText = text
    ? text.includes('.')
      ? t(text, { defaultValue: text })
      : text
    : t('common.loading')

  const sizeClasses = {
    sm: 'spinner-sm',
    md: 'spinner-md',
    lg: 'spinner-lg',
  }

  const spinnerClass = `spinner ${sizeClasses[size]}`

  // Variant-specific wrapper and styling
  switch (variant) {
    case 'fullPage':
      return (
        <div className={`min-h-screen flex items-center justify-center ${className}`}>
          <div className="text-center">
            <div className={`${spinnerClass} mx-auto mb-4`} />
            <p className="text-gray-600 font-poppins">{displayText}</p>
          </div>
        </div>
      )

    case 'section':
      return (
        <div className={`flex items-center justify-center min-h-96 ${className}`}>
          <div className="text-center">
            <div className={`${spinnerClass} mx-auto mb-4`} />
            <p className="text-gray-600 font-poppins">{displayText}</p>
          </div>
        </div>
      )

    case 'tab':
      return (
        <div className={`flex items-center justify-center min-h-64 py-12 ${className}`}>
          <div className="text-center">
            <div className={`${spinnerClass} mx-auto mb-4`} />
            <p className="text-gray-600 font-poppins">{displayText}</p>
          </div>
        </div>
      )

    case 'inline':
      return (
        <div className={`flex items-center justify-center ${className}`}>
          <div className={`${spinnerClass} mr-3`} />
          <span className="text-gray-600">{displayText}</span>
        </div>
      )

    default:
      return (
        <div className={`flex items-center justify-center min-h-96 ${className}`}>
          <div className="text-center">
            <div className={`${spinnerClass} mx-auto mb-4`} />
            <p className="text-gray-600 font-poppins">{displayText}</p>
          </div>
        </div>
      )
  }
}
